"use client";

import { useState } from "react";
import { Icon } from "./Icon";
import { extractPhone } from "@/lib/phone";

export { extractPhone };

export interface SendSmsActionProps {
  body: string; // contenu déjà rédigé (ex. réponse de Mimo) — sert de base au message
  defaultTo?: string;
  disabled?: boolean;
  onSent?: () => void;
}

type State = { kind: "idle" } | { kind: "editing"; to: string; body: string };

/** sms: URI — le séparateur avant `body=` diffère entre iOS (&) et le reste (?). */
function buildSmsHref(to: string, body: string): string {
  const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/.test(navigator.userAgent);
  const sep = isIOS ? "&" : "?";
  return `sms:${to}${sep}body=${encodeURIComponent(body)}`;
}

/** Bouton "Envoyer par SMS" — ouvre l'app SMS (téléphone, ou Phone Link sur
 *  Windows si le téléphone Android y est relié) avec le numéro et le message
 *  pré-remplis. Aucun envoi serveur : c'est l'app SMS locale qui envoie. */
export function SendSmsAction({ body, defaultTo, disabled, onSent }: SendSmsActionProps) {
  const [state, setState] = useState<State>({ kind: "idle" });

  const open = () => {
    setState({ kind: "editing", to: defaultTo ?? extractPhone(body), body });
  };

  const send = () => {
    if (state.kind !== "editing" || !state.to.trim()) return;
    window.location.href = buildSmsHref(state.to.trim(), state.body);
    setState({ kind: "idle" });
    onSent?.();
  };

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={disabled}
        className="flex items-center gap-1.5 rounded-lg border border-[#0a1410]/15 bg-white/90 backdrop-blur-xl px-3 py-1.5 text-xs font-bold text-[#0a1410] hover:bg-white shadow-[0_4px_14px_-4px_rgba(0,0,0,0.4)] hover-lift disabled:opacity-60"
      >
        <Icon name="MessageSquare" size={12} /> Envoyer par SMS
      </button>

      {state.kind === "editing" && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          onClick={() => setState({ kind: "idle" })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="flex w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-2xl max-h-[90vh]"
          >
            <header className="flex items-center justify-between gap-3 border-b border-[var(--color-line)] px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0a1410] text-white">
                  <Icon name="MessageSquare" size={14} />
                </div>
                <h2 className="text-sm font-semibold text-[#0a1410]">Envoyer par SMS</h2>
              </div>
              <button type="button" onClick={() => setState({ kind: "idle" })} className="rounded p-1 hover:bg-[var(--color-marine-50)]">
                <Icon name="X" size={16} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <p className="text-[11px] text-[var(--color-muted)]">
                Ouvre ton application SMS (téléphone, ou Phone Link si ton Android est relié à Windows) avec le message pré-rempli — l&apos;envoi se fait depuis ton téléphone.
              </p>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">Numéro</label>
                <input
                  type="tel"
                  value={state.to}
                  onChange={(e) => setState({ ...state, to: e.target.value })}
                  placeholder="06 12 34 56 78"
                  className="mt-1 w-full rounded-md border border-[var(--color-line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-marine-700)]"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">Message</label>
                <textarea
                  value={state.body}
                  onChange={(e) => setState({ ...state, body: e.target.value })}
                  rows={10}
                  maxLength={1600}
                  className="mt-1 w-full resize-y rounded-md border border-[var(--color-line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-marine-700)] font-sans"
                />
                <div className="mt-1 text-right text-[10px] text-[var(--color-muted)]">
                  {state.body.length}/1600 — {Math.max(1, Math.ceil(state.body.length / 160))} SMS
                </div>
              </div>
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-[var(--color-line)] bg-[var(--color-surface)] px-5 py-3">
              <button
                type="button"
                onClick={() => setState({ kind: "idle" })}
                className="rounded-md border border-[var(--color-line)] bg-white px-3 py-1.5 text-xs text-[#0a1410] hover:bg-[var(--color-marine-50)]"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={send}
                disabled={!state.to.trim()}
                className="flex items-center gap-1.5 rounded-md bg-[#0a1410] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0a1410]/90 disabled:opacity-50"
              >
                <Icon name="MessageSquare" size={12} /> Ouvrir l&apos;app SMS
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
